import { Injectable, computed, inject, signal } from '@angular/core';
import { MaquiService, MaquiMode, MaquiUso } from './maqui.service';

/**
 * Estado compartido del asistente Maqui (issue #648): si está habilitado y si el
 * drawer está abierto. Permite que el FAB, la pill del topbar y el drawer se
 * coordinen sin refs cruzadas entre componentes.
 */
@Injectable({ providedIn: 'root' })
export class MaquiUiService {
  private readonly maqui = inject(MaquiService);

  /** Chat operativo en la instancia (flag + API key). */
  readonly enabled = signal(false);

  /** Modo del launcher: OFF (oculto) / PROXIMAMENTE (teaser) / ON (chat). */
  readonly mode = signal<MaquiMode>('OFF');

  /** El launcher debe mostrarse (teaser o chat). */
  readonly visible = computed(() => this.mode() !== 'OFF');

  /** Modo teaser: se muestra el launcher pero el chat no funciona. */
  readonly comingSoon = computed(() => this.mode() === 'PROXIMAMENTE');

  /** Uso mensual de tokens (visibilidad de costo). Null si no aplica. */
  readonly uso = signal<MaquiUso | null>(null);

  /** Drawer abierto. */
  readonly open = signal(false);

  constructor() {
    this.maqui.estado().subscribe(e => {
      this.enabled.set(e.enabled);
      this.mode.set(e.mode);
      this.uso.set(e.uso ?? null);
    });
  }

  /**
   * Vuelve a leer el uso del mes. Se llama después de cada consulta: si no, el aviso de que se acerca el tope solo se
   * actualizaba al recargar la página, y Maqui podía cortarse sin aviso a mitad de la conversación.
   */
  refrescarUso(): void {
    this.maqui.estado().subscribe({
      next: e => {
        this.enabled.set(e.enabled);
        this.mode.set(e.mode);
        this.uso.set(e.uso ?? null);
      },
      // Un fallo al releer el uso no cambia nada: queda el último aviso conocido.
      error: () => undefined,
    });
  }

  abrir(): void { this.open.set(true); }
  cerrar(): void { this.open.set(false); }
  toggle(): void { this.open.update(v => !v); }
}
